import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './index.css';
import App from './App';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Cancellation from './pages/Cancellation';
import Wf7 from './pages/wf7';
import Wf5 from './pages/wf5';
import Wf9 from './pages/wf9';
import Wf11 from './pages/wf11';
import Wf12 from './pages/wf12';
import Wf13 from './pages/wf13';
import Wf14 from './pages/wf14';
import Wf121 from './pages/wf121';
import Wf31 from './pages/wf31';
import DialifyLanding from './dialify/pages/Landing';
import DialifyPrivacy from './dialify/pages/Privacy';
import DialifyTerms from './dialify/pages/Terms';
import DialifyWf5 from './dialify/pages/wf5';
import DialifyWf7 from './dialify/pages/wf7';
import DialifyWf9 from './dialify/pages/wf9';
import DialifyWf11 from './dialify/pages/wf11';
import DialifyWf12 from './dialify/pages/wf12';
import DialifyWf121 from './dialify/pages/wf121';
import DialifyWf13 from './dialify/pages/wf13';
import DialifyWf14 from './dialify/pages/wf14';
import DialifyWf31 from './dialify/pages/wf31';
import { initializeMetaAttribution } from './utils/metaAttribution';
import { META_PIXEL_ID } from './config/publicConfig';
import { IS_DIALIFY, SITE_VARIANT } from './config/siteVariant';
import RouteMetadata from './components/RouteMetadata';

const META_PIXEL_SCRIPT_ID = 'meta-pixel-script';
const META_PIXEL_NOSCRIPT_ID = 'meta-pixel-noscript';

// Initialize CSS variable --app-vh based on the visual viewport height to
// normalize layout across in-app browsers (e.g., Instagram/Meta) and Safari.
function initializeViewportUnitVariable() {
  const setVh = () => {
    try {
      const vv = (window as any).visualViewport;
      const height = vv?.height || window.innerHeight;
      const vh = height * 0.01;
      document.documentElement.style.setProperty('--app-vh', `${vh}px`);
    } catch (_) {
      // no-op
    }
  };
  setVh();
  const vv = (window as any).visualViewport;
  if (vv && typeof vv.addEventListener === 'function') {
    vv.addEventListener('resize', setVh);
    vv.addEventListener('scroll', setVh);
  }
  window.addEventListener('resize', setVh);
}

initializeViewportUnitVariable();
initializeMetaAttribution();
document.documentElement.dataset.siteVariant = SITE_VARIANT;

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

function ensurePixelScript() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  if (!document.getElementById(META_PIXEL_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = META_PIXEL_SCRIPT_ID;
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
  }

  if (!document.getElementById(META_PIXEL_NOSCRIPT_ID)) {
    const noscript = document.createElement('noscript');
    noscript.id = META_PIXEL_NOSCRIPT_ID;
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.head.appendChild(noscript);
  }
}

function ensureFbqStub() {
  if (typeof window === 'undefined') return;
  if (window.fbq) return;

  const fbq: any = function () {
    fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
  };
  fbq.queue = [];
  fbq.loaded = false;
  fbq.version = '2.0';
  fbq.push = fbq;
  window.fbq = fbq;
  (window as any)._fbq = fbq;
}

function hasPixelInstance(pixelId: string) {
  try {
    if (typeof window === 'undefined') return false;
    const state = window.fbq?.getState?.();
    const instances = state?.pixels ?? state?.instances;
    if (Array.isArray(instances)) {
      return instances.some((instance: any) => instance?.id === pixelId || instance?.pixelId === pixelId);
    }
  } catch (_) {
    // ignore
  }
  return false;
}

function initializeMetaPixel() {
  if (typeof window === 'undefined') return;

  ensureFbqStub();
  ensurePixelScript();

  const alreadyInitialized = hasPixelInstance(META_PIXEL_ID);
  if (!alreadyInitialized) {
    try {
      window.fbq('init', META_PIXEL_ID);
    } catch (error) {
      console.error('Meta Pixel init error:', error);
    }
  }

  // if (!pageViewTracked) {
  //   try {
  //     window.fbq('track', 'PageView');
  //     pageViewTracked = true;
  //   } catch (error) {
  //     console.error('Meta Pixel PageView error:', error);
  //   }
  // }
}

initializeMetaPixel();

const LandingPage = IS_DIALIFY ? DialifyLanding : App;
const PrivacyPage = IS_DIALIFY ? DialifyPrivacy : Privacy;
const TermsPage = IS_DIALIFY ? DialifyTerms : Terms;
const FunnelWf5 = IS_DIALIFY ? DialifyWf5 : Wf5;
const FunnelWf7 = IS_DIALIFY ? DialifyWf7 : Wf7;
const FunnelWf9 = IS_DIALIFY ? DialifyWf9 : Wf9;
const FunnelWf11 = IS_DIALIFY ? DialifyWf11 : Wf11;
const FunnelWf12 = IS_DIALIFY ? DialifyWf12 : Wf12;
const FunnelWf121 = IS_DIALIFY ? DialifyWf121 : Wf121;
const FunnelWf13 = IS_DIALIFY ? DialifyWf13 : Wf13;
const FunnelWf14 = IS_DIALIFY ? DialifyWf14 : Wf14;
const FunnelWf31 = IS_DIALIFY ? DialifyWf31 : Wf31;

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <RouteMetadata />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cancellation" element={<Cancellation />} />
        <Route path="/wf/wf5" element={<FunnelWf5 />} />
        <Route path="/wf/wf7" element={<FunnelWf7 />} />
        <Route path="/wf/wf9" element={<FunnelWf9 />} />
        <Route path="/wf/wf11" element={<FunnelWf11 />} />
        <Route path="/wf/wf12" element={<FunnelWf12 />} />
        <Route path="/wf/wf121" element={<FunnelWf121 />} />
        <Route path="/wf/wf13" element={<FunnelWf13 />} />
        <Route path="/wf/wf14" element={<FunnelWf14 />} />
        <Route path="/wf/wf31" element={<FunnelWf31 />} />
      </Routes>
    </BrowserRouter>
  </React.StrictMode>
); 
